import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  type ComponentType,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { localizedPath, type LocaleId } from '@bannermatic/core';
import { PageLoader } from '../components/ui';
import { preferredLocale, useLocale } from '../lib/i18n';
import { applyHead, type PageHead } from './head';

export interface PublicPageDef {
  /** Unprefixed path, e.g. "/" or "/privacy". */
  path: string;
  locales: readonly LocaleId[];
  component: ComponentType;
  head(locale: LocaleId, site: string): PageHead;
}

interface PublicPageState {
  path: string;
  locale: LocaleId;
  locales: readonly LocaleId[];
}

const PublicPageContext = createContext<PublicPageState | null>(null);

/** The public page being shown (null inside the app): the language menu switches between its versions. */
export function usePublicPage() {
  return useContext(PublicPageContext);
}

/** Language of the current public page (the address decides), else the interface language. */
export function usePageLocale(): LocaleId {
  const page = usePublicPage();
  const locale = useLocale((s) => s.locale);
  return page?.locale ?? locale;
}

/**
 * Link target in the language of the current public page: "/terms" becomes "/uk/terms" on a
 * Ukrainian page. Outside public pages the path is returned as is.
 */
export function useLocalePath() {
  const page = usePublicPage();
  return (path: string) => (page ? localizedPath(page.locale, path) : path);
}

/** Page body with the language and route context, without browser-only effects (pre-rendering). */
export function PublicPageView({ page, locale }: { page: PublicPageDef; locale: LocaleId }) {
  const Page = page.component;
  return (
    <PublicPageContext.Provider value={{ path: page.path, locale, locales: page.locales }}>
      <Page />
    </PublicPageContext.Provider>
  );
}

/**
 * A public page in one language. The URL decides the language; English pages (no prefix) send
 * visitors who prefer another language to their version when it exists.
 */
export function PublicRoute({ page, locale }: { page: PublicPageDef; locale: LocaleId }) {
  const location = useLocation();
  const preferred = preferredLocale();
  const redirect = locale === 'en' && preferred !== 'en' && page.locales.includes(preferred);

  // The page sets its language when it opens (not on later changes: the language menu
  // switches by opening the page in the other language).
  useLayoutEffect(() => {
    const { locale: current, setLocale } = useLocale.getState();
    // An explicit "/uk/…" address is remembered like a choice in the language menu.
    if (!redirect && current !== locale) setLocale(locale, locale !== 'en');
  }, [redirect, locale]);

  useEffect(() => {
    if (redirect) return;
    applyHead(page.head(locale, window.location.origin), {
      path: page.path,
      locale,
      locales: page.locales,
      site: window.location.origin,
    });
  }, [redirect, page, locale]);

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  if (redirect) {
    return (
      <Navigate
        replace
        to={localizedPath(preferred, page.path) + location.search + location.hash}
      />
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <PublicPageView page={page} locale={locale} />
    </Suspense>
  );
}
